import type { JourneyListsParams } from '@/types';

import { DEFAULT_RESPONSE_LIMIT } from '@/query/constants';
import axiosInstance from '@/utils/api/axios-instance';
import buildApiUrl from '@/utils/helpers/build-api-url';
import buildApiUrlWithParams from '@/utils/helpers/build-api-url-with-params';

export const API_ROUTES = {
  stationLists: () => buildApiUrl('stops'),
  stationDetails: (stopId: string | null | undefined) => buildApiUrl('stops', stopId),
  stationBoard: (stopId: string | null) => buildApiUrl('stops', stopId, 'station-board'),
  stationBoardDeviation: (stopId: string | null) =>
    buildApiUrl('deviations', 'stops', stopId, 'station-board'),
  journeyLists: ({
    date,
    status = 'all',
    page = 0,
    sort = null,
    limit = DEFAULT_RESPONSE_LIMIT,
    search = null,
  }: JourneyListsParams) =>
    buildApiUrlWithParams('journeys', {
      operation_date: date,
      skip: (page ?? 0) * (limit ?? 50),
      limit,
      status,
      sort,
      search,
    }),
  incidentLists: () => buildApiUrl('incidents'),
  journeyDetails: (routeId: string) => buildApiUrl('journeys', routeId),
  journeyStops: (id: string) => buildApiUrl('journeys', id, 'stops'),
  journeyCrews: (id: string) => buildApiUrl('journeys', id, 'crews'),
  journeyReservations: (id: string) => buildApiUrl('journeys', id, 'reservations'),
  sections: () => buildApiUrl('sections'),
  sectionStops: (sectionId: string) => buildApiUrl('sections', sectionId, 'stops'),
  journeys: (sectionId: string) => buildApiUrl('sections', sectionId, 'journeys'),
  deviation: (journeyId: string) => buildApiUrl('deviations', 'journeys', journeyId),
  sectionDeviations: (sectionId: string) =>
    buildApiUrl('deviations', 'sections', sectionId, 'journeys'),
  me: () => buildApiUrl('auth', 'me'),
  login: () => buildApiUrl('auth', 'login'),
  logout: () => buildApiUrl('auth', 'logout'),
  stationService: (stationId: string) => buildApiUrl('stops', stationId, 'services'),
  incidentsByStopId: (stopId: string) => buildApiUrl('incidents', 'stop', stopId),
  incidentsByLineNumber: (lineNumber: string) => buildApiUrl('incidents', 'route', lineNumber),
  mapConfig: buildApiUrl('user-config', 'map-config'),
  createReleaseNote: () => buildApiUrl('releases'),
  releaseNoteLists: (page = 0, limit = 20) => {
    const safeLimit = Math.min(Math.max(limit ?? 20, 1), 100);
    const safeSkip = Math.max((page ?? 0) * safeLimit, 0);

    return buildApiUrlWithParams('releases', 'all', {
      skip: safeSkip,
      limit: safeLimit,
    });
  },
  releaseNoteCheckVersion: (version: string) =>
    buildApiUrlWithParams('releases', 'check-version', { version }),
  releaseNote: (id: string | number) => buildApiUrl('releases', id),
  newReleaseNoteSeen: () => buildApiUrl('releases', 'seen'),
  publishedReleases: (page = 0, limit = 20) => {
    const safeLimit = Math.min(Math.max(limit ?? 20, 1), 100);
    const safeSkip = Math.max((page ?? 0) * safeLimit, 0);

    return buildApiUrlWithParams('releases', 'published', {
      skip: safeSkip,
      limit: safeLimit,
    });
  },
};

export const API_CLIENT = {
  infiniteJourneyLists: async (params: JourneyListsParams) => {
    // Ensure API_ROUTES.journeyLists builds the query string correctly:
    // ?status=active&limit=50&skip=0...
    const response = await axiosInstance.get(
      API_ROUTES.journeyLists({
        date: params.date,
        page: params.page,
        limit: params.limit,
        status: params.status,
        sort: params.sort,
        search: params.search,
      })
    );

    return response.data;
  },
};
